import { useState } from "react";
import { Bell, BellOff, CalendarClock, CheckCheck, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useTrendNotifySettings } from "@/hooks/useTrendNotifySettings";
import { Switch } from "@/components/ui/switch";
import { pushSupported } from "@/lib/trendPush";
import type { TrendNotifyMode } from "@/lib/trendNotifications";

const OPTIONS: { mode: TrendNotifyMode; label: string; desc: string; Icon: any }[] = [
  { mode: "instant", label: "Instant toasts", desc: "Notify me as soon as a trend is curated.", Icon: Bell },
  { mode: "daily", label: "Daily summary", desc: "One grouped toast per day instead.", Icon: CalendarClock },
  { mode: "muted", label: "Muted", desc: "No trend notifications at all.", Icon: BellOff },
];

export function NotificationSettingsPanel() {
  const {
    settings,
    unreadCount,
    setMode,
    markAllRead,
    setEmailAlerts,
    setPushAlerts,
    syncing,
  } = useTrendNotifySettings();
  const [pushBusy, setPushBusy] = useState(false);
  const canPush = pushSupported();

  const togglePush = async (next: boolean) => {
    setPushBusy(true);
    try {
      const granted = await setPushAlerts(next);
      if (next && !granted) {
        toast.error("Browser notifications blocked", {
          description: "Allow notifications for this site in your browser settings.",
        });
      }
    } finally {
      setPushBusy(false);
    }
  };


  return (
    <section
      className="rounded-xl border border-border bg-card p-5"
      data-testid="trend-notification-settings"
      aria-labelledby="trend-notify-title"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="trend-notify-title" className="font-mono text-xs text-muted-foreground">
          TREND NOTIFICATIONS
        </h2>
        <div className="flex items-center gap-2">
          {syncing && (
            <span
              data-testid="trend-notify-syncing"
              className="font-mono text-[11px] text-muted-foreground"
              role="status"
            >
              syncing…
            </span>
          )}

          <span
            data-testid="trend-unread-count"
            className={`rounded-full px-2 py-0.5 font-mono text-[11px] ${
              unreadCount > 0 ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"
            }`}
            aria-live="polite"
          >
            {unreadCount} unread
          </span>
          <button
            type="button"
            data-testid="trend-mark-all-read"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            className="flex min-h-9 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs transition hover:bg-secondary/50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <CheckCheck className="size-3.5" aria-hidden="true" />
            Mark all as read
          </button>
        </div>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Choose how you hear about newly curated trends. Your choice is saved to your profile and
        follows you across devices.
      </p>
      <div
        className="mt-3 grid gap-2 sm:grid-cols-3"
        role="radiogroup"
        aria-labelledby="trend-notify-title"
      >
        {OPTIONS.map(({ mode, label, desc, Icon }) => {
          const active = settings.mode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`trend-notify-${mode}`}
              onClick={() => setMode(mode)}
              className={`min-h-11 rounded-lg border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                active ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"
              }`}
            >
              <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase">
                <Icon className="size-3.5" aria-hidden="true" />
                {label}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{desc}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 space-y-2 border-t border-border pt-4" data-testid="trend-notify-channels">
        <p className="font-mono text-[11px] uppercase text-muted-foreground">
          Also notify me outside the dashboard
        </p>
        <label className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-border p-3">
          <span className="flex items-start gap-2">
            <Mail className="mt-0.5 size-4 text-primary" aria-hidden="true" />
            <span>
              <span className="block text-sm font-medium">Email alerts</span>
              <span className="block text-xs text-muted-foreground">
                Sends {settings.mode === "daily" ? "a daily summary email" : "an email per new trend"} to your account address.
              </span>
            </span>
          </span>
          <Switch
            data-testid="trend-notify-email-toggle"
            checked={settings.email}
            disabled={settings.mode === "muted"}
            onCheckedChange={(v) => setEmailAlerts(v)}
            aria-label="Email alerts for new curated trends"
          />
        </label>
        <label className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-border p-3">
          <span className="flex items-start gap-2">
            <Smartphone className="mt-0.5 size-4 text-primary" aria-hidden="true" />
            <span>
              <span className="block text-sm font-medium">Web push notifications</span>
              <span className="block text-xs text-muted-foreground">
                {canPush
                  ? "Browser notifications, even when the dashboard tab is closed."
                  : "This browser does not support web push notifications."}
              </span>
            </span>
          </span>
          <Switch
            data-testid="trend-notify-push-toggle"
            checked={settings.push}
            disabled={!canPush || pushBusy || settings.mode === "muted"}
            onCheckedChange={(v) => void togglePush(v)}
            aria-label="Web push notifications for new curated trends"
          />
        </label>
      </div>

      {settings.mode === "daily" && settings.pending.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground" role="status">
          {settings.pending.length} new trend{settings.pending.length === 1 ? "" : "s"} waiting for your next daily summary.
        </p>
      )}

    </section>
  );
}
